import { Signal } from "../components/SignalsStep";

/**
 * Gets the count of selected signals
 */
export function getSelectedCount(signals: Signal[]): number {
	return signals.filter((s) => s.selected).length;
}

/**
 * Gets all selected signals
 */
export function getSelectedSignals(signals: Signal[]): Signal[] {
	return signals.filter((s) => s.selected);
}

/**
 * Validates that at least one signal is selected
 */
export function validateSignalSelection(signals: Signal[]): {
	valid: boolean;
	error?: string;
} {
	const selectedCount = getSelectedCount(signals);

	if (selectedCount === 0) {
		return { valid: false, error: "Please select at least one signal" };
	}

	if (selectedCount > 3) {
		return { valid: false, error: "You can select a maximum of 3 signals" };
	}

	return { valid: true };
}

/**
 * Checks if a signal can be selected (respects max count)
 */
export function canSelectSignal(
	signals: Signal[],
	signalId: string,
	maxSelectable: number = 3,
): boolean {
	const signal = signals.find((s) => s.id === signalId);
	if (!signal) return false;

	// Can always deselect
	if (signal.selected) return true;

	// Can select if under max count
	const selectedCount = getSelectedCount(signals);
	return selectedCount < maxSelectable;
}

/**
 * Generates a unique ID for a new signal
 */
export function generateSignalId(): string {
	return `signal_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Creates a new signal with default values
 */
export function createNewSignal(
	title: string = "New Signal",
	description: string = "",
): Signal {
	return {
		id: generateSignalId(),
		title,
		description,
		selected: false,
		isEditing: false,
	};
}

/**
 * Toggles the selected state of a signal
 */
export function toggleSignal(
	signals: Signal[],
	signalId: string,
	maxSelectable: number = 3,
): Signal[] {
	return signals.map((signal) => {
		if (signal.id === signalId) {
			// Can always deselect
			if (signal.selected) {
				return { ...signal, selected: false };
			}

			// Can select only if under max count
			const selectedCount = getSelectedCount(signals);
			if (selectedCount < maxSelectable) {
				return { ...signal, selected: true };
			}

			// Can't select - already at max
			return signal;
		}
		return signal;
	});
}

/**
 * Updates a signal field
 */
export function updateSignal(
	signals: Signal[],
	signalId: string,
	field: string,
	value: any,
): Signal[] {
	return signals.map((signal) => {
		if (signal.id === signalId) {
			return { ...signal, [field]: value };
		}
		return signal;
	});
}

/**
 * Adds a new signal to the list
 */
export function addSignal(signals: Signal[], newSignal: Signal): Signal[] {
	return [...signals, newSignal];
}

/**
 * Removes a signal from the list
 */
export function removeSignal(signals: Signal[], signalId: string): Signal[] {
	return signals.filter((s) => s.id !== signalId);
}

/**
 * Sanitizes signal text (trim, normalize whitespace)
 */
export function sanitizeSignalText(text: string): string {
	return text
		.trim()
		.replace(/\s+/g, " ")
		.replace(/\n\s*\n/g, "\n");
}
